// Thin wrapper around Cloudflare Turnstile's vanilla script.
// We avoid pulling in @marsidev/react-turnstile to keep deps unchanged.
// The site key comes from the per-env config dict; dev uses Cloudflare's
// always-passes test key so local dev works without a real Turnstile setup.

import React, { useEffect, useRef } from "react";
import config from "../../config";

const TURNSTILE_SCRIPT_URL =
    "https://challenges.cloudflare.com/turnstile/v0/api.js";
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";

declare global {
    interface Window {
        turnstile?: {
            render: (
                el: HTMLElement,
                options: {
                    sitekey: string;
                    callback?: (token: string) => void;
                    "error-callback"?: () => void;
                    "expired-callback"?: () => void;
                    theme?: "light" | "dark" | "auto";
                    size?: "normal" | "compact" | "flexible";
                },
            ) => string;
            reset: (widgetId?: string) => void;
            remove: (widgetId: string) => void;
        };
    }
}

let scriptLoading: Promise<void> | null = null;
function ensureTurnstileScript(): Promise<void> {
    if (typeof window === "undefined") return Promise.reject();
    if (window.turnstile) return Promise.resolve();
    if (scriptLoading) return scriptLoading;

    scriptLoading = new Promise<void>((resolve, reject) => {
        const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
        if (existing) {
            existing.addEventListener("load", () => resolve());
            existing.addEventListener("error", () => reject());
            return;
        }
        const script = document.createElement("script");
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject();
        document.head.appendChild(script);
    });
    return scriptLoading;
}

interface Props {
    onToken: (token: string | null) => void;
    theme?: "light" | "dark" | "auto";
    size?: "normal" | "compact" | "flexible";
}

export function TurnstileWidget({ onToken, theme = "auto", size = "normal" }: Props) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const sitekey = config.TURNSTILE_SITE_KEY as string;

        ensureTurnstileScript()
            .then(() => {
                if (cancelled || !containerRef.current || !window.turnstile) return;
                widgetIdRef.current = window.turnstile.render(
                    containerRef.current,
                    {
                        sitekey,
                        callback: (token: string) => onToken(token),
                        "error-callback": () => onToken(null),
                        "expired-callback": () => onToken(null),
                        theme,
                        size,
                    },
                );
            })
            .catch(() => {
                onToken(null);
            });

        return () => {
            cancelled = true;
            if (widgetIdRef.current && window.turnstile) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                } catch {
                    // ignore
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <div ref={containerRef} />;
}
